<script>
  import { createEventDispatcher } from 'svelte'
  import { recentWorkouts } from '../stores/app.js'
  import { format } from 'date-fns'
  import Templates from './Templates.svelte'
  import ExerciseLibrary from './ExerciseLibrary.svelte'

  const dispatch = createEventDispatcher()

  let activeTab = 'workouts' // 'workouts' | 'templates' | 'exercises' tab

  function getTypeEmoji(type) {
    const emojis = {
      strength: '💪',
      cardio: '🏃',
      flexibility: '🧘',
      sports: '⚽',
      other: '📋'
    }
    return emojis[type] || '📋'
  }

  function getTypeName(type) {
    const names = {
      strength: '重量訓練',
      cardio: '有氧運動',
      flexibility: '伸展/瑜珈',
      sports: '運動競技',
      other: '其他'
    }
    return names[type] || '其他'
  }

  function handleEdit(workoutId) {
    dispatch('navigate', { view: 'workoutForm', options: { mode: 'edit', workoutId } })
  }

  async function handleDelete(workout) {
    const date = new Date(workout.date)
    const typeName = getTypeName(workout.type)
    const dateStr = format(date, 'yyyy/MM/dd')

    const confirmMessage = `確定要刪除這筆訓練記錄嗎？\n\n${typeName} - ${dateStr}\n\n此操作無法復原。`

    if (confirm(confirmMessage)) {
      dispatch('delete', { workoutId: workout.id })
    }
  }
</script>

<div class="container mx-auto px-2 py-4 pb-24">
  <main>
    <!-- Tabs -->
    <div class="flex border-b border-gray-200 mb-6">
      <button
        class="px-4 py-2 font-medium transition-colors {activeTab === 'workouts' ? 'text-primary-600 border-b-2 border-primary-600' : 'text-gray-600 hover:text-gray-800'}"
        on:click={() => activeTab = 'workouts'}
      >
        訓練記錄
      </button>
      <button
        class="px-4 py-2 font-medium transition-colors {activeTab === 'templates' ? 'text-primary-600 border-b-2 border-primary-600' : 'text-gray-600 hover:text-gray-800'}"
        on:click={() => activeTab = 'templates'}
      >
        訓練模板
      </button>
      <button
        class="px-4 py-2 font-medium transition-colors {activeTab === 'exercises' ? 'text-primary-600 border-b-2 border-primary-600' : 'text-gray-600 hover:text-gray-800'}"
        on:click={() => activeTab = 'exercises'}
      >
        運動項目
      </button>
    </div>

    {#if activeTab === 'workouts'}
    <!-- Recent Workouts -->
    <div class="mt-8">
      <div class="flex justify-between items-center mb-4">
        <h2 class="text-2xl font-semibold">最近的訓練</h2>
        <button
          on:click={() => dispatch('navigate', { view: 'workoutList' })}
          class="btn btn-secondary"
        >
          查看歷史記錄
        </button>
      </div>
      <div class="space-y-4">
        {#if $recentWorkouts.length === 0}
          <p class="text-gray-600">尚無訓練記錄</p>
        {:else}
          {#each $recentWorkouts as workout (workout.id)}
            {@const date = new Date(workout.date)}
            <div class="card hover:shadow-md transition-shadow">
              <div class="flex justify-between items-start">
                <div class="flex-1">
                  <div class="flex items-center gap-2 mb-2">
                    <span class="text-2xl">{getTypeEmoji(workout.type)}</span>
                    <span class="font-semibold text-gray-800">{getTypeName(workout.type)}</span>
                    {#if !workout.synced}
                      <span class="text-xs bg-orange-100 text-orange-600 px-2 py-1 rounded">待同步</span>
                    {/if}
                  </div>
                  <div class="text-sm text-gray-600">
                    {format(date, 'yyyy/MM/dd')} {format(date, 'HH:mm')}
                  </div>
                  {#if workout.duration}
                    <div class="text-sm text-gray-600">{Math.floor(workout.duration / 60)} 分鐘</div>
                  {/if}
                  {#if workout.notes}
                    <div class="text-sm text-gray-500 mt-1">{workout.notes}</div>
                  {/if}
                </div>
                <div class="flex gap-2">
                  <button
                    on:click={() => handleEdit(workout.id)}
                    class="text-blue-600 hover:text-blue-800 text-sm font-medium"
                  >
                    編輯
                  </button>
                  <button
                    on:click={() => handleDelete(workout)}
                    class="text-red-600 hover:text-red-800 text-sm font-medium"
                  >
                    刪除
                  </button>
                </div>
              </div>
            </div>
          {/each}
        {/if}
      </div>
    </div>

    {:else if activeTab === 'templates'}
    <!-- Templates View -->
    <Templates on:navigate={(e) => dispatch('navigate', e.detail)} />

    {:else if activeTab === 'exercises'}
    <!-- Exercise Library View -->
    <ExerciseLibrary />
    {/if}

    <!-- Floating Add Button (only show on workouts tab) -->
    {#if activeTab === 'workouts'}
    <button
      on:click={() => dispatch('navigate', { view: 'workoutForm', options: { mode: 'create' } })}
      class="fixed right-4 bottom-24 z-10 btn btn-primary rounded-full"
    >
      +
    </button>
    {/if}
  </main>
</div>
